import { useState, useMemo } from "react";
import { MapPin, Wind, Fuel, Zap, Activity, ChevronRight, ChevronLeft } from "lucide-react";
import { AircraftRentalDto } from "@/types";
import { useNavigation } from "@/context/NavigationContext";
import { useAuth } from "@/context/AuthContext"; // 🟢 Înlocuit/Importat hook-ul de autentificare

interface AircraftRentalCardProps {
  rental: AircraftRentalDto;
}

// 🟢 Funcție utilitară pentru a detecta valori nule, nedefinite sau placeholder-e de tip "N/A"
const isInvalidValue = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  const str = String(value).trim().toLowerCase();
  return str === "" || str === "null" || str === "undefined" || str === "n/a" || str === "na";
};

const AircraftRentalCard = ({ rental }: AircraftRentalCardProps) => {
  const { navigateTo } = useNavigation();
  const { myAircraftRentals } = useAuth(); // 🟢 Extrage închirierile utilizatorului curent
  const [currentIndex, setCurrentIndex] = useState(0);

  // 🟢 Verifică dacă această închiriere aparține utilizatorului logat
  const isOwnListing = useMemo(() => {
    if (!myAircraftRentals || !rental.rentalId) return false;
    return myAircraftRentals.some((myRental) => myRental.rentalId === rental.rentalId);
  }, [myAircraftRentals, rental.rentalId]);

  const images = useMemo(() => {
        // 1. Direct path: Use streaming public URLs from DiskMediaStorageEngine if returned
        if (rental.mediaUrls && rental.mediaUrls.length > 0) {
          return rental.mediaUrls.filter(url => url && url.trim() !== "");
        }

        // 2. Fallback path: Retain backward compatibility for older Base64 parsing models
        if (rental.media && rental.media.length > 0) {
          return rental.media.map((m) => {
            const data = (m as any).mediaData;
            if (!data) return "";

            if (data.startsWith("iVBOR")) return `data:image/png;base64,${data}`;
            if (data.startsWith("/9j/")) return `data:image/jpeg;base64,${data}`;

            return data.startsWith("data:") ? data : `data:image/jpeg;base64,${data}`;
          }).filter(img => img !== "");
        }

        // 3. Fallback Layout Placeholder: Structural dynamic fallback template
        return [`https://via.placeholder.com/400x300?text=${encodeURIComponent(rental.model || "Rental")}`];
      }, [rental.mediaUrls, rental.media, rental.model]);

  // Rezolvare label-uri și curățare valori
  const cleanManufacturer = isInvalidValue(rental.manufacturer) ? "" : rental.manufacturer;
  const cleanModel = isInvalidValue(rental.model) ? "Aeronavă Închiriat" : rental.model;
  const cleanBaseAirport = isInvalidValue(rental.baseAirport) ? null : rental.baseAirport;
  const cleanCategory = isInvalidValue(rental.category) ? "General" : rental.category;
  const cleanRegistration = isInvalidValue(rental.registration) ? null : rental.registration;
  const cleanPricingType = isInvalidValue(rental.pricingType) ? "-" : rental.pricingType;
  const cleanBillingMethod = isInvalidValue(rental.billingMethod) ? "-" : rental.billingMethod;

  const hudItems = useMemo(() => {
      return [
        {
          id: "flight-regime",
          value: rental.isIfrCertified ? "IFR" : "VFR",
          label: "Regim Zbor",
          colorClass: rental.isIfrCertified ? "text-emerald-400" : "text-amber-500"
        },
        {
          id: "pricing-type",
          value: cleanPricingType,
          label: "Tip Tarif",
          colorClass: "text-blue-400"
        },
        {
          id: "billing-method",
          value: cleanBillingMethod,
          label: "Contorizare",
          colorClass: "text-slate-300 font-mono"
        }
      ];
    }, [rental.isIfrCertified, cleanPricingType, cleanBillingMethod]);

  const handleCardClick = () => {
      navigateTo("rental-details", {
        rentalId: rental.rentalId,
        aircraftRental: rental,
        seller: isOwnListing ? 1 : 0
      });
    };

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  return (
    <div
      onClick={handleCardClick}
      className={`group relative bg-[#0F172A]/80 backdrop-blur-xl border rounded-3xl overflow-hidden cursor-pointer hover:border-blue-500/50 transition-all duration-300 shadow-2xl ${
        isOwnListing ? "border-dashed border-slate-700 opacity-95" : "border-slate-800"
      }`}
    >
      {/* IMAGE SECTION WITH HUD OVERLAY */}
      <div className="relative h-56 overflow-hidden">
        <img
          src={images[currentIndex]}
          alt={cleanModel}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          onError={(e) => {
            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=Image+Unavailable';
          }}
        />

        {/* Top Badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-2 z-10 max-w-[calc(100%-120px)]">
          {/* 🟢 Adăugat: Badge pentru listarea proprie, stilizat pe albastru premium */}
          {isOwnListing && (
            <span className="bg-slate-900/90 backdrop-blur-md border border-blue-500/30 text-blue-400 text-[9px] font-black px-2 py-1 rounded-md uppercase tracking-widest shadow-lg animate-pulse">
              Propria listare
            </span>
          )}
          <span className="bg-blue-600 text-white text-[9px] font-black px-2 py-1 rounded-md uppercase tracking-widest shadow-lg">
            {cleanCategory}
          </span>
          {rental.isIfrCertified && (
            <span className="bg-emerald-600 text-white text-[9px] font-black px-2 py-1 rounded-md uppercase tracking-widest shadow-lg">
              IFR
            </span>
          )}
        </div>

        {/* Tail Number Badge */}
        {/* 🟢 MODIFICAT: Se ascunde complet badge-ul de înregistrare dacă este invalid */}
        {cleanRegistration && (
          <div className="absolute top-3 right-3 bg-slate-900/90 backdrop-blur-md border border-slate-700 px-2 py-1 rounded-md z-10">
            <p className="text-[10px] font-mono font-bold text-blue-400">{cleanRegistration}</p>
          </div>
        )}

        {/* Carousel Controls */}
        {images.length > 1 && (
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={prevImage} className="p-1.5 bg-slate-900/80 rounded-full text-white hover:bg-blue-600">
              <ChevronLeft size={16} />
            </button>
            <button onClick={nextImage} className="p-1.5 bg-slate-900/80 rounded-full text-white hover:bg-blue-600">
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* CONTENT SECTION */}
      <div className="p-5 space-y-4">
        {/* Title & Base */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Activity size={14} className="text-blue-500 animate-pulse" />
            <h3 className="text-lg font-black text-white tracking-tight uppercase truncate">
              {cleanManufacturer ? (
                <>
                  {cleanManufacturer} <span className="text-slate-500">{cleanModel}</span>
                </>
              ) : (
                cleanModel
              )}
            </h3>
          </div>
          {/* 🟢 MODIFICAT: Se ascunde locația dacă este nulă/incompletă */}
          {cleanBaseAirport && (
            <div className="flex items-center gap-1 text-slate-500">
              <MapPin size={12} />
              <span className="text-[11px] font-bold uppercase tracking-wider">{cleanBaseAirport}</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 border-y border-slate-800/50 py-3 bg-white/[0.01] rounded-lg items-center">
                  {hudItems.map((item, index) => (
                    <div
                      key={item.id}
                      className={`text-center ${index > 0 ? "border-l border-slate-800/50" : ""}`}
                    >
                      <p className={`text-xs font-black uppercase tracking-wider ${item.colorClass}`}>
                        {item.value}
                      </p>
                      <p className="text-[8px] text-slate-500 font-bold uppercase tracking-tighter mt-0.5">
                        {item.label}
                      </p>
                    </div>
                  ))}
                </div>

        {/* PRICING FOOTER */}
        <div className="flex items-end justify-between">
          <div>
            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.2em]">Hourly Rate</p>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-black text-white tracking-tighter">
                €{isInvalidValue(rental.grossValue) ? "LA CERERE" : rental.grossValue}
              </span>
              {!isInvalidValue(rental.grossValue) && cleanPricingType !== "-" && (
                <span className="text-[10px] font-bold text-blue-500">{cleanPricingType}</span>
              )}
            </div>
          </div>
          {cleanBillingMethod !== "-" && (
            <div className="text-right">
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Billing</p>
              <p className="text-xs font-mono font-bold text-white">{cleanBillingMethod}</p>
            </div>
          )}
        </div>
      </div>

      {/* DECORATIVE LINE */}
      <div className="absolute bottom-0 left-0 h-1 bg-blue-600 transition-all duration-300 w-0 group-hover:w-full" />
    </div>
  );
};

export default AircraftRentalCard;